import {
  ComboboxChevron,
  type ComboboxData,
  type ComboboxItem,
  Select,
  type SelectProps
} from "@mantine/core";
import classNames from "classnames";
import { useState } from "react";
import type { CommonFormFieldItemProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { FormFieldRightSection } from "../FormFieldRightSection";

interface FormFieldSelectWithCreateProps
  extends
    CommonFormFieldItemProps,
    Omit<SelectProps, "data" | "value" | "onChange"> {
  type: "select-with-create";
  onCreate: (value: string) => void;
  data?: ComboboxData | Array<Omit<ComboboxItem, "value"> & { value: number }>;
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
}

function FormFieldSelectWithCreate({
  helpText,
  highlight,
  isBetaFeature,
  type,
  warned,
  data,
  onCreate,
  value,
  onChange,
  ...fieldSpecificProps
}: FormFieldSelectWithCreateProps) {
  const [searchValue, setSearchValue] = useState<string>("");

  function handleCreation() {
    if (onCreate && searchValue.trim() !== "") {
      onCreate(searchValue.trim());
      setSearchValue("");
    }
  }

  const useNumbers =
    Array.isArray(data) &&
    typeof data[0] === "object" &&
    "value" in data[0] &&
    typeof data[0].value === "number";

  const parsedData: SelectProps["data"] = useNumbers
    ? (data as Array<{ value: number; label: string }>).map((item) => ({
        value: item.value.toString(),
        label: item.label
      }))
    : (data as ComboboxData);

  const parsedValue =
    useNumbers && typeof value === "number"
      ? value.toString()
      : (value as string | null);

  function handleChangeWithParsing(newValue: string | null) {
    if (useNumbers) {
      const numericValue = newValue === null ? null : parseInt(newValue);
      onChange?.(numericValue);
    } else {
      onChange?.(newValue);
    }
  }

  const manualRightSectionWidth = warned
    ? fieldSpecificProps.clearable
      ? 76
      : 60
    : undefined;

  return (
    <Select
      {...fieldSpecificProps}
      aria-label={
        typeof fieldSpecificProps.label === "string"
          ? fieldSpecificProps.label
          : undefined
      }
      className={classNames(
        "FormFieldSelectWithCreate",
        fieldSpecificProps.className
      )}
      data={parsedData}
      defaultValue={undefined}
      label={
        fieldSpecificProps.label && (
          <FormFieldLabel
            helpText={helpText}
            highlight={highlight}
            isBetaFeature={isBetaFeature}
            label={fieldSpecificProps.label}
            required={fieldSpecificProps.required}
          />
        )
      }
      labelProps={{ w: "100%", ...fieldSpecificProps.labelProps }}
      nothingFoundMessage={
        !fieldSpecificProps.nothingFoundMessage
          ? "Drücken Sie Enter, um das gesuchte Element hinzuzufügen"
          : fieldSpecificProps.nothingFoundMessage
      }
      rightSection={
        (warned || fieldSpecificProps.rightSection) && (
          <FormFieldRightSection
            rightSection={
              <>
                {fieldSpecificProps.rightSection}
                <ComboboxChevron />
              </>
            }
            warned={warned}
          />
        )
      }
      searchable
      searchValue={searchValue}
      styles={{
        section: {
          width: fieldSpecificProps.rightSectionWidth || manualRightSectionWidth
        }
      }}
      value={parsedValue}
      onChange={handleChangeWithParsing}
      onKeyDown={(event) => {
        if (
          event.key === "Enter" &&
          !data?.some((item) =>
            typeof item === "string"
              ? item === searchValue
              : "label" in item && item.label === searchValue
          )
        ) {
          handleCreation();
        }
      }}
      onSearchChange={(value) => setSearchValue(value)}
    />
  );
}

export { FormFieldSelectWithCreate, FormFieldSelectWithCreateProps };
